// A purchase window as the audience editor holds it (C2, Gio 2026-10-17): either a rolling
// "in the last N days", or fixed calendar days in New York from 2021 on, whose end is a day
// or "today". Pure: `components/purchase-window-picker.tsx` renders it and
// `audience-visual-rules.ts` turns it into the `recency` / `last_purchase_between` /
// `purchase_history` / `purchase_history_between` rules.

import { AUDIENCE_MAX_WITHIN_DAYS, AUDIENCE_PURCHASE_HISTORY_START } from '@/api/types.gen';
import { dayYmd, fmtDateInput, fmtDay } from './datetime';

export type PurchaseWindow =
  | { mode: 'last'; days: number }
  /** `to: null` is "through today": the window keeps rolling forward with the calendar. */
  | { mode: 'between'; from: string; to: string | null };

/** The first day any window may reach (New York). */
export const PURCHASE_HISTORY_START: string = AUDIENCE_PURCHASE_HISTORY_START;

/** The rolling windows offered as one-tap choices. */
export const PURCHASE_WINDOW_CHIPS: readonly { days: number; label: string }[] = [
  { days: 30, label: '30 days' },
  { days: 90, label: '90 days' },
  { days: 180, label: '6 months' },
  { days: 365, label: '1 year' },
  { days: 730, label: '2 years' },
];

/** "Since Jan 2021" is a fixed start, not a day count: a count rolls by elapsed time and
 * would miss the first hours of January 1st in New York. */
export const SINCE_HISTORY_START: PurchaseWindow = { mode: 'between', from: PURCHASE_HISTORY_START, to: null };

const DAY = /^\d{4}-\d{2}-\d{2}$/;

/** Today's calendar day in New York, `YYYY-MM-DD`. */
export function todayInNewYork(now: Date = new Date()): string {
  return fmtDateInput(now);
}

/** The calendar day `days` before `day`. */
export function dayMinus(day: string, days: number): string {
  return dayYmd(Date.parse(`${day}T00:00:00Z`) - days * 86_400_000);
}

/** Whole days from one calendar day to another (`to - from`). */
export function daysBetween(from: string, to: string): number {
  return Math.round((Date.parse(`${to}T00:00:00Z`) - Date.parse(`${from}T00:00:00Z`)) / 86_400_000);
}

/** The widest rolling window that still starts on or after 2021-01-01. */
export function maxRollingDays(today: string): number {
  return Math.min(AUDIENCE_MAX_WITHIN_DAYS, Math.max(1, daysBetween(PURCHASE_HISTORY_START, today)));
}

const LEGACY_WINDOWS: Readonly<Record<string, number>> = {
  '7 days': 7,
  '30 days': 30,
  '60 days': 60,
  '90 days': 90,
  '6 months': 180,
  '1 year': 365,
  '2 years': 730,
};

/**
 * Read any value a rule row may hold as a window: the object this module writes, or the
 * label string the editor wrote before (`'2 years'`, `'45 days'`), which a draft can still
 * carry. Anything unreadable becomes an empty rolling window that validation names.
 */
export function purchaseWindowFromValue(value: unknown): PurchaseWindow {
  if (typeof value === 'string') {
    const label = value.trim().toLowerCase();
    const known = LEGACY_WINDOWS[label];
    if (known !== undefined) return { mode: 'last', days: known };
    const match = label.match(/^(\d+)\s+(day|days|month|months|year|years)$/);
    if (!match) return { mode: 'last', days: 0 };
    const unit = match[2]!;
    return { mode: 'last', days: Number(match[1]) * (unit.startsWith('month') ? 30 : unit.startsWith('year') ? 365 : 1) };
  }
  if (value !== null && typeof value === 'object' && !Array.isArray(value)) {
    const record = value as Record<string, unknown>;
    if (record.mode === 'between') {
      return {
        mode: 'between',
        from: typeof record.from === 'string' ? record.from : '',
        // A cleared end date stays '' (an error to fix), never null: only "to today" may
        // remove the upper bound, or a closed lapsed-buyer range would quietly reopen.
        to: typeof record.to === 'string' ? record.to : null,
      };
    }
    if (record.mode === 'last') {
      const days = Number(record.days);
      return { mode: 'last', days: Number.isFinite(days) ? days : 0 };
    }
  }
  return { mode: 'last', days: 0 };
}

/** What is wrong with a window today, in words, or null. Mirrors `Rule::validate_window_at`. */
export function purchaseWindowIssue(window: PurchaseWindow, today: string = todayInNewYork()): string | null {
  if (window.mode === 'last') {
    if (!Number.isInteger(window.days) || window.days < 1) return 'Enter a number of days, 1 or more.';
    if (window.days > maxRollingDays(today)) {
      return `Enter at most ${maxRollingDays(today).toLocaleString()} days (back to January 1, 2021).`;
    }
    return null;
  }
  if (!DAY.test(window.from)) return 'Choose the first day.';
  if (window.from < PURCHASE_HISTORY_START) return 'Purchases are kept from January 1, 2021.';
  if (window.to !== null && !DAY.test(window.to)) return 'Choose the last day, or tick "to today".';
  const end = window.to ?? today;
  if (end > today) return 'The last day cannot be in the future.';
  if (window.from > end) return 'The first day must be on or before the last day.';
  return null;
}

/** `2023-03-01` as `Mar 1, 2023`. */
export function formatPurchaseDay(day: string): string {
  return DAY.test(day) ? fmtDay(day) : day;
}

/** A short reading of a window: "in the last 2 years", "Mar 1, 2023 – today". */
export function describePurchaseWindow(window: PurchaseWindow): string {
  if (window.mode === 'last') {
    const chip = PURCHASE_WINDOW_CHIPS.find((entry) => entry.days === window.days);
    return `in the last ${chip?.label ?? `${window.days.toLocaleString()} days`}`;
  }
  return `${formatPurchaseDay(window.from)} – ${window.to === null ? 'today' : formatPurchaseDay(window.to)}`;
}
